import {
  getCurrentUi,
  setRowDataToFormData,
} from 'designer/component/action/Data'
import { getFormGroup } from 'designer/component/action/FormGroup'
import objectPath from 'object-path'
import { resetKey } from 'designer/component/element/button/ButtonAction'
import { getFormGroupResetKeyList } from 'designer/component/element/button/ResetFormGroup'
import httpClient from 'utility/httpClient.util'

const selectRowCompositionCache = {}

const getCurrentUiByKey = (key, jsonForm) => {
  let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
  if (ui?.key === key) return ui

  let keyList = `${key || ''}`.split('.')
  let shortKey = keyList[keyList.length - 1]
  if (!shortKey || shortKey === key) return ui

  return getCurrentUi(shortKey, jsonForm.schema, jsonForm.uischema)
}

const resetFormGroupData = (form = {}, formData = {}, designContext = {}) => {
  let { jsonForm } = designContext
  let {
    isGroup,
    groupData = [],
    mapParam = {},
    mapArray = {},
  } = form
  let paramList = Object.keys(mapParam)

  paramList.forEach((key) => {
    let isArray = objectPath.get(mapArray, key)
    if (!isArray) return

    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (ui.key !== key) return

    let { elements = [] } = ui
    elements.forEach((mainElement) => {
      let name = mainElement.name
      if (mainElement.tagname === 'panel') {
        name = '@' + name
      }
      objectPath.del(formData, name)
      objectPath.del(formData, `@isCheckedAll.${name}`)
    })
  })

  if (isGroup) {
    groupData.forEach((group) => {
      formData = resetKey({
        keyList: getFormGroupResetKeyList(group),
        formData,
        designContext,
      })
    })
  } else {
    formData = resetKey({
      keyList: getFormGroupResetKeyList(form),
      formData,
      designContext,
    })
  }

  return formData
}

const setFormDataKey = (keyList = {}, key) => {
  if (key === undefined || key === null || key === '') return
  if (typeof key === 'boolean') return
  keyList[key] = true
}

const setFormDataKeyList = (keyList = {}, data = {}) => {
  Object.keys(data || {}).forEach((key) => {
    setFormDataKey(keyList, key)
    setFormDataKey(keyList, data[key])
    let valueKeyList = String(data[key] || '').split('.')
    if (valueKeyList.length > 1) {
      setFormDataKey(keyList, valueKeyList[valueKeyList.length - 1])
    }
  })
}

const getFormGroupDataKeyList = (form = {}) => {
  let { mapRadio = {} } = form
  let keyList = getFormGroupResetKeyList(form)

  setFormDataKeyList(keyList, mapRadio)

  return keyList
}

const getSelectRowFormDataKeyList = (form = {}) => {
  if (!form.isGroup) return getFormGroupDataKeyList(form)

  let keyList = {}
  let groupData = form.groupData || []
  groupData.forEach((group) => {
    keyList = {
      ...keyList,
      ...getFormGroupDataKeyList(group),
    }
  })

  return keyList
}

const getRowDataValue = ({ rowData = {}, jsonKey = '', key = '' }) => {
  let keyList = String(key || '').split('.')
  let shortKey = keyList[keyList.length - 1]
  let rowDataKeyList = []

  if (jsonKey && key) rowDataKeyList.push(`${jsonKey}.${key}`)
  if (key) rowDataKeyList.push(key)
  if (jsonKey && shortKey && shortKey !== key) {
    rowDataKeyList.push(`${jsonKey}.${shortKey}`)
  }
  if (shortKey && shortKey !== key) rowDataKeyList.push(shortKey)

  let rowDataKey = rowDataKeyList.find((rowDataKey) =>
    objectPath.has(rowData, rowDataKey),
  )
  if (!rowDataKey) return { hasValue: false }

  let value = objectPath.get(rowData, rowDataKey)
  if (value === 'true') value = true
  else if (value === 'false') value = false
  else if (value === 'null') value = null

  return {
    hasValue: true,
    value,
  }
}

const hasAdditionalFormGroupRowData = (
  form = {},
  rowData = {},
  compositionData = {},
) => {
  let keyList = getSelectRowFormDataKeyList(form)

  return Object.keys(keyList).some((key) => {
    let currentValue = getRowDataValue({ rowData, key })
    let compositionValue = getRowDataValue({ rowData: compositionData, key })

    return !currentValue.hasValue && compositionValue.hasValue
  })
}

const getSelectRowCompositionQuery = (rowData = {}) => {
  let params = new URLSearchParams()
  ;[
    'vn',
    'hn',
    'an',
    'compositionId',
    'ehrId',
    'uidVersionId',
    'oldUidVersionId',
  ].forEach((key) => {
    let value = objectPath.get(rowData, key)
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value)
    }
  })

  let query = params.toString()
  return query ? `?${query}` : ''
}

const getSelectRowCompositionData = async (rowData = {}) => {
  let templateId = objectPath.get(rowData, 'templateId')
  if (!templateId) return {}

  let query = getSelectRowCompositionQuery(rowData)
  let cacheKey = `${templateId}${query}`
  if (selectRowCompositionCache[cacheKey]) {
    return selectRowCompositionCache[cacheKey]
  }

  let response = await httpClient
    .get(`/ehr/composition/${encodeURIComponent(templateId)}${query}`)
    .catch(() => null)
  let responseData = response?.data?.data || response?.data || {}
  let compositionData =
    responseData?.data || responseData?.composition || responseData
  if (!compositionData || typeof compositionData !== 'object') {
    compositionData = {}
  }

  selectRowCompositionCache[cacheKey] = compositionData
  return compositionData
}

const getSelectRowData = async (form = {}, rowData = {}) => {
  if (!objectPath.get(rowData, 'templateId')) return rowData

  let compositionData = await getSelectRowCompositionData(rowData)
  if (!hasAdditionalFormGroupRowData(form, rowData, compositionData)) {
    return rowData
  }

  let tableData = rowData.tableData
  let rowId = rowData.id
  let nextRowData = {
    ...rowData,
    ...compositionData,
  }

  if (tableData !== undefined) objectPath.set(nextRowData, 'tableData', tableData)
  if (rowId !== undefined) objectPath.set(nextRowData, 'id', rowId)

  return nextRowData
}

const setRowDataToFormGroupData = (form = {}, request = {}) => {
  let {
    jsonForm = {},
    formData = {},
    rowData = {},
    isUiChanged,
  } = request
  let { jsonKey = '', formComponentName } = form
  let keyList = getFormGroupDataKeyList(form)

  Object.keys(keyList).forEach((key) => {
    let { hasValue, value } = getRowDataValue({ rowData, jsonKey, key })
    if (!hasValue) return

    let dataKey = formComponentName ? `${formComponentName}.${key}` : key
    objectPath.set(formData, dataKey, value)

    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema, isUiChanged)
    if (ui?.key !== key || !ui?.name || ui.name === key) return

    let dataName = formComponentName
      ? `${formComponentName}.${ui.name}`
      : ui.name
    objectPath.set(formData, dataName, value)
  })

  return formData
}

export const updateSelectRowFormData = ({
  form = {},
  formData = {},
  rowData = {},
  designContext = {},
  resetData = false,
}) => {
  let { jsonForm, isUiChanged } = designContext

  if (resetData) {
    formData = resetFormGroupData(form, formData, designContext)
  }
  if (form.isGroup) {
    let groupData = form.groupData || []
    groupData.forEach((subGroup) => {
      formData = setRowDataToFormData(subGroup, {
        jsonForm,
        formData,
        rowData,
        isUiChanged,
      })
      formData = setRowDataToFormGroupData(subGroup, {
        jsonForm,
        formData,
        rowData,
        isUiChanged,
      })
    })
  } else {
    formData = setRowDataToFormData(form, {
      jsonForm,
      formData,
      rowData,
      isUiChanged,
    })
    formData = setRowDataToFormGroupData(form, {
      jsonForm,
      formData,
      rowData,
      isUiChanged,
    })
  }

  return formData
}

const addSelectRowRuleKey = (keys = [], key = '') => {
  if (!key) return keys
  if (keys.includes(key)) return keys

  keys.push(key)
  return keys
}

const addSelectRowRuleKeyList = (keys = [], keyList = {}) => {
  Object.keys(keyList || {}).forEach((key) => addSelectRowRuleKey(keys, key))

  return keys
}

const addSelectRowMapParamRuleKeys = (keys = [], mapParam = {}) => {
  Object.keys(mapParam || {}).forEach((key) => {
    let name = mapParam[key]
    let nameList = String(name || '').split('.')

    addSelectRowRuleKey(keys, key)
    addSelectRowRuleKey(keys, name)
    addSelectRowRuleKey(keys, nameList[nameList.length - 1])
  })

  return keys
}

const getSelectRowFormRuleKeys = (form = {}) => {
  if (!form.isGroup) {
    return addSelectRowMapParamRuleKeys(
      addSelectRowRuleKeyList([], getFormGroupDataKeyList(form)),
      form.mapParam,
    )
  }

  return (form.groupData || []).reduce(
    (keys, group) =>
      addSelectRowMapParamRuleKeys(
        addSelectRowRuleKeyList(keys, getFormGroupDataKeyList(group)),
        group.mapParam,
      ),
    [],
  )
}

const getSelectRowRuleKeys = (form = {}, formData = {}, nextFormData = {}) => {
  let ruleKeys = getSelectRowFormRuleKeys(form)

  return Object.keys(nextFormData || {}).reduce((keys, key) => {
    if (objectPath.get(formData, key) === objectPath.get(nextFormData, key)) {
      return keys
    }

    return addSelectRowRuleKey(keys, key)
  }, ruleKeys)
}

export const handleRowSelect = (
  checked,
  rowData,
  { designContext, uischema },
) => {
  let { isUiChanged, setIsUiChanged } = designContext
  let rowSelection =
    objectPath.get(isUiChanged, ['tableSelection', uischema.key]) || []
  if (checked) {
    rowSelection.push(rowData)
  } else {
    let unselectIndex = rowSelection.findIndex(
      (select) => select?.id === rowData?.id,
    )
    if (unselectIndex > -1) {
      objectPath.del(rowSelection, unselectIndex)
    }
  }
  objectPath.set(isUiChanged, ['tableSelection', uischema.key], rowSelection)
  setIsUiChanged(isUiChanged)
}

export const updateTabSelectRow = async ({
  rowAction,
  rowData,
  formData,
  designContext,
}) => {
  let {
    setFormData,
    jsonForm,
    tabParamActive,
    setTabParamActive,
    isUiChanged,
    setIsUiChanged,
    setTabActiveState,
  } = designContext
  let { formDataKey, tabSelect, tabPanelSelect } = rowAction
  let selectRowRulesData = null
  if (formDataKey) {
    let prevFormData = { ...formData }
    const formGroup = getFormGroup(designContext)
    let form = formGroup.find(({ key }) => key === formDataKey)
    if (!form) return
    rowData = await getSelectRowData(form, rowData)
    formData = updateSelectRowFormData({
      form,
      formData,
      rowData,
      designContext,
      resetData: true,
    })
    objectPath.set(jsonForm, 'formData', formData)
    selectRowRulesData = {
      formData,
      rowData,
      ruleUiKeys: getSelectRowRuleKeys(form, prevFormData, formData),
    }
    setFormData(formData)
  }
  if (tabSelect) {
    let tabUi = getCurrentUiByKey(tabSelect, jsonForm)
    if (!tabUi?.key) return
    let tabPanelUi = getCurrentUiByKey(tabPanelSelect, jsonForm)
    let tabPanelKey = tabPanelUi?.key || tabPanelSelect
    if (!tabPanelUi?.name) return
    if (selectRowRulesData) {
      objectPath.set(selectRowRulesData, 'tabKey', tabPanelKey)
    }
    let { param = [] } = tabUi
    let filterParam = param.filter((param) => param.tabKey === tabPanelKey)
    let tabState = {
      isActiveTab: true,
      tabKey: tabPanelKey,
      tabPanelUi,
      selectRowRulesData,
    }
    if (setTabActiveState) {
      setTabActiveState({
        tabKeys: [tabUi.key, tabSelect],
        tabPanelName: tabPanelUi.name,
        filterParam,
        shouldSetParam: true,
        tabState,
      })
    } else {
      objectPath.set(tabParamActive, tabUi.key, filterParam)
      if (tabSelect !== tabUi.key) {
        objectPath.set(tabParamActive, tabSelect, filterParam)
      }
      setTabParamActive(tabParamActive)
      objectPath.set(isUiChanged, ['activeTab', tabUi.key], tabPanelUi.name)
      if (tabSelect !== tabUi.key) {
        objectPath.set(isUiChanged, ['activeTab', tabSelect], tabPanelUi.name)
      }
      objectPath.set(isUiChanged, tabUi.key, tabState)
      if (tabSelect !== tabUi.key) {
        objectPath.set(isUiChanged, tabSelect, {
          isActiveTab: true,
          tabKey: tabPanelKey,
          tabPanelUi,
          selectRowRulesData,
        })
      }
      setIsUiChanged({ ...isUiChanged })
    }
  }
}
